import * as vp from 'vopay';
import { Icon, Box, Title, formatAsCurrency, capitalizeFirstLetterOfWords, GetColorStatus, Card, Pagination } from 'vopay';
import { Button, TableFiltersContainer, TransactionStatus, Table } from 'UI';
import { format, subWeeks } from 'date-fns';

import { FunctionComponent, useState, useMemo } from 'react';

import { usePartnerData } from '@/providers/PartnerData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import TransactionsSearchForm from './TransactionsSearchForm';
import { useTranslation } from 'react-i18next';

type TransactionListProps = {
  areDatesValid: boolean;
  transactions: vp.AccountTransactionsMap;
  isExportingCSV: boolean;
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  pageCount: number;
  pageIndex: number;
  initialFilters?: vp.ParentAccountTransactionsRequest;
  initialTimeRange?: [vp.Timestamp, vp.Timestamp];
  handlers: {
    onTransactionsSearch: (values: vp.ParentAccountTransactionsRequest) => void;
    handleDate: (values: [vp.Timestamp, vp.Timestamp] | undefined) => void;
    exportCSV: () => void;
    handlePageChange: (selectedPage: number) => void;
  };
  setShouldShowEndDateError?: (event: boolean) => void;
};

type ParentAccountTransactionsRequest = {
  FromDate: string;
  ToDate: string;
  VopayAccountID?: string;
  Currency?: string;
  TransactionType?: string;
  Status?: string;
  ExportCSV?: boolean;
  TransactionID?: string;
};

type FC = FunctionComponent<TransactionListProps>;

const PartnerAccountList: FC = (props: TransactionListProps) => {
  const {
    transactions,
    handlers,
    areDatesValid,
    hasInvalidEndDate,
    shouldShowEndDateError,
    setShouldShowEndDateError,
    pageCount,
    pageIndex,
    initialFilters,
    initialTimeRange,
  } = props;

  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountTransactions' });
  const { getModuleTitle } = useNavMenu();

  const {
    response: { isRoleViewOnly, accountId: loggedInAccountId },
  } = usePartnerData();

  const { permissions } = usePermissions();
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp] | undefined>(initialTimeRange);
  const [filtersApplied, setFiltersApplied] = useState(!!initialFilters);

  const [searchValues, setSearchValues] = useState<ParentAccountTransactionsRequest>({
    FromDate: initialFilters?.FromDate ?? format(subWeeks(new Date(), 1), 'yyyy-MM-dd'),
    ToDate: initialFilters?.ToDate ?? format(new Date(), 'yyyy-MM-dd'),
    Currency: initialFilters?.Currency ?? '',
    TransactionType: initialFilters?.TransactionType ?? '',
    VopayAccountID: initialFilters?.VopayAccountID ?? '',
    Status: initialFilters?.Status ?? '',
    TransactionID: initialFilters?.TransactionID ?? '',
  });

  const memoizedSearchValues = useMemo(() => searchValues, [searchValues]);

  const tableProps: vp.TableProps<vp.Transaction> = {
    id: 'partner_account-transaction-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'id',
        header: t('Table.id'),
        align: 'left',
        template: ({ data }) =>
          shouldShowView(data) ? (
            <Table.Link
              to={{
                pathname: `/accounts/${removeUsernameWhiteSpace(data)}/${removeWhiteSpace(
                  data,
                )}/transaction/${data.TransactionID}`,
                search: new URLSearchParams({
                  type: data.TransactionType,
                  accountName: data.AccountName,
                }).toString(),
              }}
            >
              {data.TransactionID}
            </Table.Link>
          ) : (
            <>{data.TransactionID}</>
          ),
      },
      {
        id: 'accountName',
        header: t('Table.accountName'),
        align: 'left',
        field: 'AccountName',
      },
      {
        id: 'partnerName',
        header: t('Table.partnerAccountName', { defaultValue: 'Partner Account Name' }),
        align: 'left',
        visible: permissions.PartnerCreationEnabled,
        template: ({ data }) => {
          if (data.PartnerAccountID && data.PartnerAccountID === loggedInAccountId) {
            return (
              <>
                {data.PartnerAccountName} • {t('Table.direct', { defaultValue: 'Direct' })}
              </>
            );
          }

          return data.PartnerAccountID ? (
            <Table.Link to={{ pathname: `/partners/${data.PartnerAccountID}` }}>
              {data.PartnerAccountName}
            </Table.Link>
          ) : (
            <>{data.PartnerAccountName}</>
          );
        },
      },
      {
        id: 'referenceNumber',
        header: t('Table.referenceNumber'),
        align: 'left',
        field: 'ClientReferenceNumber',
      },
      {
        id: 'date',
        header: t('Table.date'),
        align: 'left',
        field: 'TransactionDateTime',
      },
      {
        id: 'name',
        header: t('Table.name'),
        align: 'left',
        field: 'FullName',
      },
      {
        id: 'type',
        header: t('Table.type'),
        align: 'left',
        field: 'TransactionType',
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus
            status={GetColorStatus(data.TransactionStatus)}
            label={capitalizeFirstLetterOfWords(data.TransactionStatus || '')}
          />
        ),
      },
      {
        id: 'currency',
        header: t('Table.currency'),
        align: 'left',
        field: 'Currency',
      },
      {
        id: 'transactionAmount',
        header: t('Table.transactionAmount'),
        align: 'left',
        template: ({ data }) =>
          parseFloat(data.DebitAmount) ? formatAsCurrency(data.DebitAmount) : formatAsCurrency(data.CreditAmount),
      },
      {
        id: 'holdAmount',
        header: t('Table.holdAmount'),
        align: 'left',
        template: ({ data }) => formatAsCurrency(data.HoldAmount),
      },
      {
        id: 'causeOfFailure',
        header: t('Table.causeOfFailure'),
        align: 'left',
        field: 'TransactionFailureReason',
      },
      {
        id: 'flagStatus',
        header: t('Table.flagStatus'),
        align: 'left',
        template: ({ data }) => (!data.ConfirmedByClient ? data.TransactionFlag : ''),
      },
      {
        id: 'notes',
        header: t('Table.notes'),
        align: 'left',
        field: 'Notes',
      },
    ],
    rows: Object.values(transactions?.transactionList || {}).map((transaction) => ({
      id: transaction.TransactionID,
      data: transaction,
    })),
  };

  function shouldShowView({ TransactionType }: vp.Transaction) {
    const isEFT = [vp.TransactionType.EFT_FUNDING, vp.TransactionType.EFT_WITHDRAWAL].includes(TransactionType);

    const isInteract = [vp.TransactionType.INTERACT_MONEY_REQUEST, vp.TransactionType.INTERACT_BULK_PAYOUT].includes(
      TransactionType,
    );

    const isCreditCard = [vp.TransactionType.CREDIT_CARD].includes(TransactionType);

    return isEFT || isInteract || isCreditCard;
  }

  function removeWhiteSpace({ TransactionType }: vp.Transaction) {
    return decodeURI(TransactionType).replaceAll(' ', '');
  }

  function removeUsernameWhiteSpace({ Username }: vp.Transaction) {
    return decodeURI(Username).replaceAll(' ', '');
  }

  const search = (values: vp.ParentAccountTransactionsRequest) => {
    setSearchValues(values);
    handlers.onTransactionsSearch(values);
    const fromDate = timeRange?.[0] != null ? format(new Date(timeRange[0]), 'yyyy-MM-dd') : '';
    const toDate = timeRange?.[1] != null ? format(new Date(timeRange[1]), 'yyyy-MM-dd') : '';

    setFiltersApplied(
      !!values.Currency ||
        !!values.TransactionType ||
        !!values.Status ||
        !!values.VopayAccountID ||
        !!values.VopayAccountID ||
        fromDate !== format(subWeeks(new Date(), 1), 'yyyy-MM-dd') ||
        toDate !== format(new Date(), 'yyyy-MM-dd'),
    );
  };

  return (
    <Box flx="col" gapSize="36px">
      <Box ai="center" jc="sb">
        <Title title={getModuleTitle()} />

        <Button
          id="account-transactions-export"
          onClick={handlers.exportCSV}
          disabled={!areDatesValid || isRoleViewOnly}
        >
          {t('exportButton')}
        </Button>
      </Box>

      <Card
        style={{
          padding: '16px',
        }}
      >
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <TableFiltersContainer.Trigger />

          <TableFiltersContainer.Content>
            <TransactionsSearchForm
              timeRange={timeRange}
              setTimeRange={setTimeRange}
              onTransactionsSearch={search}
              handlers={handlers}
              hasInvalidEndDate={hasInvalidEndDate}
              shouldShowEndDateError={shouldShowEndDateError}
              setShouldShowEndDateError={setShouldShowEndDateError}
              defaultValues={memoizedSearchValues}
            />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>

        <Table {...tableProps} />

        <Box flx="row" jc="center" mt="md" base="8">
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => {
                handlers.handlePageChange(e.selected);
              }}
            />
          )}
        </Box>

        {Object.keys(transactions).length === 0 && (
          <Box flx="col" jc="center" mt="md">
            <Box ai="center" jc="center">
              <Icon icon="search2" size="lg" />
            </Box>

            <Box ai="center" jc="center" pt="md">
              {t('searchInstruction')}
            </Box>
          </Box>
        )}

        {transactions?.transactionList?.length === 0 && (
          <Box flx="col" jc="center" mt="md">
            <Box ai="center" jc="center" pt="md">
              {t('noRecords')}
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default PartnerAccountList;
